/**
 * @fileoverview Workspace creation page
 * @description Page for creating a new workspace after email verification
 * @author Taqat AI Team
 * @since 2024-12-09
 */

import * as React from "react";
import { useForm } from "react-hook-form";
import { safeZodResolver, onFormValidationError } from "@/utils/safe-zod-resolver";
import { z } from "zod";
import { useNavigate, useSearchParams } from "react-router-dom";
import logoImage from "@/assets/images/logo.png";
import logoImageDark from "@/assets/images/logo-dark.png";
import { Button } from "@/components/ui/button";
import { FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth, useAuthActions } from "@/store";
import { workspaceStore } from "@/store/slices/workspace-slice";
import { api } from "@/utils/api-client";
import { setAuthTokens, setUserData } from "@/utils/auth-storage";
import { getReturnUrl } from "@/utils/return-url";

/**
 * Validation schema for workspace creation form
 */
const createWorkspaceSchema = z.object({
  name: z
    .string()
    .min(1, "Workspace name is required")
    .max(100, "Workspace name must be less than 100 characters")
    .regex(
      /^[a-zA-Z0-9\s\-_']+$/,
      "Workspace name can only contain letters, numbers, spaces, hyphens, underscores, and apostrophes"
    ),
  description: z
    .string()
    .max(500, "Description must be less than 500 characters")
    .optional(),
});

/**
 * Form data type for workspace creation
 */
type CreateWorkspaceFormData = z.infer<typeof createWorkspaceSchema>;

/**
 * Workspace creation page component
 */
export function CreateWorkspacePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { setUser, setLoading: setAuthLoading } = useAuthActions();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const form = useForm<CreateWorkspaceFormData>({
    resolver: safeZodResolver(createWorkspaceSchema as any),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  /**
   * Guards against submitting the form more than once.
   *
   * `isLoading` disables the button, but React only applies that on the next
   * render — three clicks in the same tick all reach `onSubmit` first and fire
   * three `POST /workspaces`. A unique constraint stops the duplicates from
   * being persisted, so the extra requests come back 500 rather than creating
   * anything, but they are still two failed writes per impatient click. A ref
   * is readable synchronously, so it closes the window the state flag can't.
   */
  const submitGuardRef = React.useRef(false);

  /**
   * Handles workspace creation form submission
   */
  const onSubmit = React.useCallback(
    async (data: CreateWorkspaceFormData) => {
      if (submitGuardRef.current) {
        return;
      }
      submitGuardRef.current = true;

      setIsLoading(true);
      setError(null);
      setAuthLoading(true);

      try {
        // Call workspace creation API
        const response = await api.post("/workspaces", data, {
          requiresAuth: true,
          showSuccessToast: false,
          showErrorToast: false,
        });
        const result = response.data;
        console.log("Workspace creation response:", result);

        if (response.success) {
          // Update auth store with user data if provided
          if (result.user) {
            setUser(result.user);
          }

          // Update workspace store with the new workspace data
          const workspaceActions = workspaceStore.getState();

          // Set all workspaces if provided
          if (result.workspaces && Array.isArray(result.workspaces)) {
            workspaceActions.setWorkspaces(result.workspaces);
          }

          // Set the selected workspace
          if (result.selectedWorkspace) {
            workspaceActions.setCurrentWorkspace(result.selectedWorkspace);
          }

          // Store session token if provided
          if (result.session?.sessionToken) {
            const tokens = {
              accessToken: result.session.sessionToken,
              refreshToken: result.session.refreshToken || "",
              expiresAt: new Date(result.session.expires).getTime(),
            };

            setAuthTokens(tokens);
            setUserData(result.user);
          }

          // Honour a pending destination (a deep-link the user was sent to
          // before they had any workspace), else land on the apps page.
          navigate(getReturnUrl(searchParams) ?? "/app", { replace: true });
        } else {
          setError(result.error?.message || "Failed to create workspace");
        }
      } catch (err) {
        console.error("Error creating workspace:", err);
        setError(
          err instanceof Error ? err.message : "Failed to create workspace"
        );
        // Only reopen the guard on failure: a successful create navigates away,
        // and re-arming it there would let a late click submit again.
        submitGuardRef.current = false;
      } finally {
        setIsLoading(false);
        setAuthLoading(false);
      }
    },
    [navigate, searchParams, setUser, setAuthLoading]
  );

  React.useEffect(() => {
    if (user && user.name) {
      form.setValue("name", `${user.name} Workspace`);
    } else {
      form.setValue("name", "My Workspace");
    }
  }, [user?.name]);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background decoration - same as signin page */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 lg:w-96 lg:h-96 bg-primary-100 dark:bg-primary-900/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
        <div className="absolute top-40 right-10 w-72 h-72 lg:w-96 lg:h-96 bg-secondary-100 dark:bg-secondary-900/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
        <div className="absolute -bottom-8 left-20 w-72 h-72 lg:w-96 lg:h-96 bg-primary-50 dark:bg-primary-950/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        {/* Workspace Creation Card - centered like signin page */}
        <div className="w-full max-w-md">
          <div className="bg-card shadow-xl rounded-2xl border border-border p-8">
            {/* Logo inside the card */}
            <div className="flex justify-center mb-6">
              <img
                src={logoImage}
                alt="Taqat AI Logo"
                className="h-10 w-auto block dark:hidden"
              />
              <img
                src={logoImageDark}
                alt="Taqat AI Logo"
                className="h-10 w-auto hidden dark:block"
              />
            </div>

            <div className="text-center mb-6">
              <h1 className="text-2xl font-semibold tracking-tight">
                Create Workspace
              </h1>
              <p className="text-sm text-muted-foreground">
                Set up your workspace to get started
              </p>
            </div>

            <form
              onSubmit={form.handleSubmit(onSubmit, onFormValidationError)}
              className="space-y-4"
            >
              <FormItem>
                <Label htmlFor="name">Workspace Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter workspace name"
                  {...form.register("name")}
                  disabled={isLoading}
                  autoComplete="organization"
                />
                <FormMessage>
                  {form.formState.errors.name?.message}
                </FormMessage>
              </FormItem>

              <FormItem>
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Describe your workspace..."
                  rows={3}
                  {...form.register("description")}
                  disabled={isLoading}
                  className="resize-none"
                />
                <FormMessage>
                  {form.formState.errors.description?.message}
                </FormMessage>
              </FormItem>

              {error && (
                <div className="rounded-md bg-red-50 dark:bg-red-950/50 p-3 border border-red-200 dark:border-red-800">
                  <div className="text-sm text-red-800 dark:text-red-300">{error}</div>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Creating workspace..." : "Create Workspace"}
              </Button>

              <div className="text-center text-xs text-muted-foreground">
                You can always change these settings later in your workspace
                preferences.
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

